import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

const ConfirmBox = ({
  successText = "Confirm",
  rejectText,
  onSuccess,
  open,
  onReject,
  title,
  subTitle,
  subTitleColor,
  variant = "default", // "default", "warning", "success", "info", "danger"
  icon,
  showCloseButton = true,
  size = "md", // "sm", "md", "lg"
}) => {
  // Icon mapping based on variant
  const getIcon = () => {
    if (icon) return icon;
    
    switch (variant) {
      case "warning":
        return <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />;
      case "success":
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case "info":
        return <InformationCircleIcon className="h-6 w-6 text-blue-500" />;
      case "danger":
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      default:
        return null;
    }
  };

  // Size configurations
  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg"
  };

  // Button variant styles
  const getButtonStyles = (type) => {
    const baseStyles = "inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white";
    
    if (type === "primary") {
      switch (variant) {
        case "danger":
          return `${baseStyles} bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm`;
        case "success":
          return `${baseStyles} bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-sm`;
        case "warning":
          return `${baseStyles} bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500 shadow-sm`;
        default:
          return `${baseStyles} bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-500 shadow-sm`;
      }
    } else {
      return `${baseStyles} bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500 shadow-sm`;
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onReject || (() => {})}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all`}>
                {/* Header */}
                <div className="relative px-6 pt-6 pb-4">
                  {showCloseButton && onReject && (
                    <button
                      type="button"
                      className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={onReject}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                  
                  <div className="flex items-start space-x-4">
                    {getIcon() && (
                      <div className="flex-shrink-0">
                        {getIcon()}
                      </div>
                    )}
                    
                    <div className="flex-1 text-left">
                      <Dialog.Title className="text-lg font-semibold text-gray-900 leading-6">
                        {title}
                      </Dialog.Title>
                      
                      {subTitle && (
                        <div className="mt-2">
                          <p 
                            className="text-sm leading-5"
                            style={{ 
                              color: subTitleColor || "#6B7280" 
                            }}
                          >
                            {subTitle}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
                  {rejectText && (
                    <button
                      type="button"
                      className={getButtonStyles("secondary")}
                      onClick={onReject}
                    >
                      {rejectText}
                    </button>
                  )}
                  
                  <button
                    type="button"
                    className={getButtonStyles("primary")}
                    onClick={onSuccess}
                  >
                    {successText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmBox;
